import { Icon } from "./Icon";
import { useEffect, useRef } from "react";

/**
 * THE UNSAVED-CHANGES QUESTION, AS A CENTRED MODAL OVER A DIMMED PAGE.
 *
 * 🔴 HIS WORD, 2026-09-19 ~21:3x, WITH THEIR SCREENSHOT BESIDE OURS: *"you see though what shows
 * when we have made changes and tap the x on the workout edit? it's in the middle and everything
 * else gets dimmed to draw attention. ours i didn't even see it first time. so fix it like that,
 * like everfit."*
 *
 * ⚠️ AND THE THING HE REPORTED IS THE WHOLE POINT: OURS WAS CORRECT AND UNNOTICEABLE. The
 * question was a callout inside the builder's own footer, in the same ink as the problems list
 * one line above it, under a window still holding a workout's worth of content — so the eye that
 * had just pressed Close had no reason to travel there. A question nobody sees is answered by
 * whatever they click next, which for a dirty draft is the one outcome the rule exists to
 * prevent.
 *
 * ⚠️ IT IS A SECOND NATIVE `<dialog>` OVER THE FIRST, AND THE INERTNESS IS THE FEATURE HERE.
 * `WorkoutOverviewWindow` refuses a nested dialog for its own confirm, and its reasoning is
 * sound THERE: that question is about the workout drawn behind it, so greying the evidence out
 * at the moment it is the reason for the decision costs the coach the thing they are deciding
 * about. This question is the opposite shape — it is about the EDITS, not about anything drawn
 * on screen, and "everything else gets dimmed to draw attention" is exactly what he asked for.
 * Two confirms, two answers, and the difference is stated rather than discovered later.
 *
 * ⚠️ ESCAPE DISMISSES THE QUESTION, IT DOES NOT ANSWER IT — unchanged, and now enforced by the
 * browser rather than by us: Escape reaches the TOP modal, which is this one, and its `cancel`
 * is routed back through `closeAttempt` so the rule in `lib/builder.ts` stays the one authority
 * on what a close gesture does. No number of Escapes reaches a discard.
 *
 * ⚠️ FOCUS LANDS ON THE SAFE ANSWER, FROM AN EFFECT. The first cut of the old version asked for
 * focus inside the close handler under `requestAnimationFrame` — which reads as "after the next
 * paint" and is not: the frame can run before React has committed the render that CREATES the
 * button, so the ref is null and the call is a silent no-op. An effect keyed on `open` runs
 * after the commit by construction. That ordering is pinned by a ruler and is kept here.
 */
export function DiscardWindow({ open, onCancel, onDiscard, keep }: {
  /** Whether the question is being asked. The caller's own `confirming` state, never a copy. */
  readonly open: boolean;
  /** Take the question away and keep editing. Routed through `closeAttempt` by the caller. */
  readonly onCancel: () => void;
  /** The one labelled act that throws the draft away. */
  readonly onDiscard: () => void;
  /**
   * 🔴 W85 — THE WORKOUT BUILDER'S FORM OF THE SAME QUESTION. The builder saves as you type, so when its ✕ asks, the edits
   * are ALREADY written: the question is not "discard what is unsaved" but "keep what you did, or put it back". So its
   * safe answer is `Keep` (leave, everything saved) and `Discard changes` RESTORES the workout as it opened — `sentence`
   * says which, in one line. Escape still dismisses the question (the caller's `closeAttempt`), never answers it.
   * Absent for every other editor, whose question is unchanged.
   */
  readonly keep?: {
    readonly sentence: string; readonly onKeep: () => void; readonly busy: boolean;
    /** W95 — the workout builder saves only on Save, so its question is `Save your changes?` · `Save`; the exercise editor
     *  (still saving as you type) keeps `Keep your changes?` · `Keep`. */
    readonly title?: string; readonly label?: string;
  };
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  /* `showModal()` on an already-open dialog THROWS and `close()` on a closed one is a no-op that
     still fires nothing — so both directions are guarded rather than assumed, the same shape the
     three windows above this one use. */
  useEffect(() => {
    const el = dialogRef.current;
    if (el === null) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  useEffect(() => { if (open) cancelRef.current?.focus(); }, [open]);

  return (
    <dialog ref={dialogRef} className="ht-window ht-confirm" aria-labelledby="discard-title"
            onCancel={(e) => { e.preventDefault(); onCancel(); }}
            /* ENTER IS SAVE, wherever the focus is in the question (it starts on Save) — except on `Discard changes`
               itself, where the key presses the button the coach moved to. Escape stays `onCancel`: back to editing. */
            onKeyDown={(e) => {
              if (keep === undefined || e.key !== "Enter" || keep.busy) return;
              if ((e.target as HTMLElement).closest(".ht-confirm-discard") !== null) return;
              e.preventDefault();
              keep.onKeep();
            }}>
      <div className="ht-window-frame ht-confirm-frame">
        {/* Their red ⓘ, drawn rather than typed: a glyph would take the reader's font and the
            circle would sit off the baseline. `currentColor` so the one colour declaration lives
            in the stylesheet — ruler 3 bans a hex anywhere under `web/` but the generated sheet.
            ⚠️ `aria-hidden`: the heading under it says the same thing in words, and colour and
            shape carry nothing on their own (the design system's rule, and WCAG's). */}
        <Icon name="info" className="ht-confirm-mark" size={30} />
        {/* An `h2` for the same reason the builder's title is one: the page underneath owns the
            document's `h1`, and a modal that claims it leaves the outline with two. */}
        <h2 className="ht-title ht-confirm-title" id="discard-title">
          {keep === undefined ? "Discard changes?" : (keep.title ?? "Keep your changes?")}
        </h2>
        <p className="ht-confirm-sentence">
          {keep === undefined ? "Are you sure you want to go? Changes have not been saved yet." : keep.sentence}
        </p>
        <div className="ht-confirm-actions">
          {keep === undefined
            ? <button type="button" ref={cancelRef} className="ht-button ht-confirm-cancel"
                      onClick={onCancel}>Cancel</button>
            : <button type="button" ref={cancelRef} className="ht-button ht-button-primary ht-confirm-cancel"
                      disabled={keep.busy} onClick={keep.onKeep}>{keep.label ?? "Keep"}</button>}
          {/* W96's pass (his word): where Save is the answer, Save is the ONE filled control and `Discard changes` is the
              OUTLINED red of every other destructive control (`.ht-button-danger`) — two filled buttons side by side read
              as two equal answers. The bare discard question keeps its filled red (`.ht-confirm-go`, his 2026-09-19). */}
          <button type="button"
                  className={keep === undefined ? "ht-button ht-button-danger ht-confirm-go" : "ht-button ht-button-danger ht-confirm-discard"}
                  disabled={keep?.busy === true} onClick={onDiscard}>Discard changes</button>
        </div>
      </div>
    </dialog>
  );
}
